import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef, useMemo } from "react";
import { useLocation } from "wouter";

interface PublicLayoutOptions {
  preFooter?: ReactNode;
  /**
   * Set when the page renders a fixed bottom CTA bar on small screens:
   * the layout adds matching bottom padding under the footer so its last
   * rows (copyright/privacy links) are not permanently covered by the bar.
   * The value is the Tailwind breakpoint at which the page hides the bar.
   */
  mobileStickyBar?: "md" | "lg";
}

interface CrmLayoutOptions {
  pageTitle?: string;
  title?: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  headerActions?: ReactNode;
}

interface LayoutContextValue {
  publicOptions: PublicLayoutOptions;
  crmOptions: CrmLayoutOptions;
  setPublicOptions: (options: PublicLayoutOptions) => void;
  setCrmOptions: (options: CrmLayoutOptions) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [publicOptions, setPublicOptionsState] = useState<PublicLayoutOptions>({});
  const [crmOptions, setCrmOptionsState] = useState<CrmLayoutOptions>({});

  const setPublicOptions = useCallback((options: PublicLayoutOptions) => {
    setPublicOptionsState(options);
  }, []);

  const setCrmOptions = useCallback((options: CrmLayoutOptions) => {
    setCrmOptionsState(options);
  }, []);

  return (
    <LayoutContext.Provider value={{ 
      publicOptions, 
      crmOptions, 
      setPublicOptions, 
      setCrmOptions
    }}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayoutContext() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayoutContext must be used within LayoutProvider");
  }
  return context;
}

export function usePublicLayout(options: PublicLayoutOptions) {
  const { setPublicOptions } = useLayoutContext();
  const [location] = useLocation();
  const locationRef = useRef(location);
  
  useEffect(() => {
    locationRef.current = location;
    setPublicOptions(options);
    return () => setPublicOptions({});
  }, [location]); // Update when route changes (component mounts/unmounts on route)
}

export function useCrmLayout(options: CrmLayoutOptions) {
  const { setCrmOptions } = useLayoutContext();
  const [location] = useLocation();
  const locationRef = useRef(location);
  
  useEffect(() => {
    locationRef.current = location;
    setCrmOptions(options);
    return () => setCrmOptions({});
  }, [location]); // Update when route changes (component mounts/unmounts on route)
}
